// OCR API service for travel ticket processing
// Uses Vista OCR service with validate-only mode
import { validateDocument, VISTA_CASE_TYPES, VISTA_DOCUMENT_TYPES } from './vistaOcrService';
import {
  storeTravelFile,
  TRAVEL_FILE_KEYS,
} from '../utils/travelFilesStorage';

/**
 * OCR service for processing travel flight tickets
 * Uses Vista OCR service with validate-only mode
 */
class OCRService {
  /**
   * Process flight ticket files using Vista OCR
   * Uses validate-only mode and stores file for later submission
   * @param {File} file - The ticket file to process (PDF, JPG, PNG)
   * @returns {Promise<Object>} OCR processing result
   */
  async processFlightTicket(file) {
    try {
      // Validate input
      if (!file || !(file instanceof File || file instanceof Blob)) {
        throw new Error('Invalid file provided');
      }

      // Log file details for debugging
      console.log('[Vista] Processing flight ticket:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      // Use Vista OCR with validate-only mode
      const result = await validateDocument(
        file,
        VISTA_DOCUMENT_TYPES.TRAVEL.FLIGHT_TICKET,
        VISTA_CASE_TYPES.TRAVEL
      );

      if (!result.success) {
        return result;
      }

      // Store file for later Vista submission
      storeTravelFile(TRAVEL_FILE_KEYS.FLIGHT_TICKET, file);
      console.log('[Vista] Flight ticket processed and stored');

      // Validate response structure
      const ocrData = result.data;

      if (!ocrData || !ocrData.output) {
        throw new Error('Invalid OCR response format');
      }

      return {
        success: true,
        data: ocrData,
        rawResponse: result.rawResponse,
      };
    } catch (error) {
      // Categorize errors for better handling
      let errorType = 'UNKNOWN_ERROR';
      let errorMessage = error.message;

      if (error.code === 'ECONNABORTED') {
        errorType = 'TIMEOUT_ERROR';
        errorMessage = 'OCR processing timed out';
      } else if (error.message.includes('AUTH_ERROR')) {
        errorType = 'AUTH_ERROR';
        errorMessage = 'Authentication failed';
      } else if (error.message.includes('BAD_REQUEST')) {
        errorType = 'BAD_REQUEST';
        errorMessage = 'Invalid file or request format';
      } else if (error.message.includes('SERVER_ERROR')) {
        errorType = 'SERVER_ERROR';
        errorMessage = 'OCR service is temporarily unavailable';
      } else if (error.message.includes('not configured')) {
        errorType = 'CONFIG_ERROR';
        errorMessage = error.message;
      }

      console.error('[Vista] Flight Ticket Processing Error:', {
        type: errorType,
        message: errorMessage,
        originalError: error.message,
      });

      return {
        success: false,
        error: {
          type: errorType,
          message: errorMessage,
          details: error.message,
        },
      };
    }
  }

  /**
   * Transform OCR data to application format
   * @param {Object} ocrData - Raw OCR response data
   * @returns {Object} Formatted travel data
   */
  transformOCRData(ocrData) {
    try {
      const output = ocrData.output;

      if (!output || !output.status) {
        throw new Error('OCR processing was not successful');
      }

      const flights = output.data?.flights || [];
      const numberOfPassengers = output.data?.numberOfPassengers || 1;
      const durationInDays = output.data?.durationOfTravelInDays || 1;

      // Find departure and return flights
      let departureDate = null;
      let returnDate = null;
      let destinations = [];
      let airlines = [];
      let flightNumbers = [];

      if (flights.length > 0) {
        // Get departure date from first flight
        departureDate = new Date(flights[0].departureDate);

        // Get return date from last flight
        returnDate = new Date(flights[flights.length - 1].arrivalDate);

        // Collect destinations, airlines, and flight numbers
        flights.forEach((flight) => {
          if (flight.to) destinations.push(flight.to);
          if (flight.airline) airlines.push(flight.airline);
          if (flight.flight) flightNumbers.push(flight.flight);
        });
      }

      // Determine plan type based on number of passengers
      const planType = numberOfPassengers > 1 ? 'family' : 'individual';

      // Create primary flight number (first flight or combined)
      const primaryFlightNumber = flightNumbers.length > 0 ? flightNumbers.join(' + ') : 'Multiple Flights';

      return {
        flightNumber: primaryFlightNumber,
        fromDate: departureDate,
        toDate: returnDate,
        planType: planType,
        numberOfPassengers: numberOfPassengers,
        durationInDays: durationInDays,
        destinations: [...new Set(destinations)], // Remove duplicates
        airlines: [...new Set(airlines)], // Remove duplicates
        allFlights: flights,
        ocrProcessed: true,
        ocrTimestamp: Date.now(),
      };
    } catch (error) {
      console.error('Error transforming OCR data:', error);
      throw new Error('Failed to process OCR results: ' + error.message);
    }
  }
}

// Create singleton instance
const ocrService = new OCRService();

// Export service methods
export const processFlightTicket = (file) => ocrService.processFlightTicket(file);
export const transformOCRData = (ocrData) => ocrService.transformOCRData(ocrData);
export default ocrService;
